import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import moment from 'moment';
import { useIntl, FormattedMessage } from 'react-intl';
import { Helmet } from 'react-helmet-async';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import UstBannerAd from './components/ads/UstBannerAd';
import MatchedContentAd from './components/ads/MatchedContentAd';
import {
  Container,
  Row,
  Col,
  Link,
  Breadcrumb, BreadcrumbItem,
  Button
} from './StyledComponents.js';
import { useCsEvent } from './client/useEvents';

// Styled components
const MonthContainer = styled.div`
  margin-bottom: 2rem;
`;

const MonthTitle = styled.h3`
  margin: 0.25rem 0;
  font-size: 1.5rem;
  font-weight: 500;
  border-bottom: 1px solid #d4e2e6;
  padding-bottom: 0.5rem;
`;

const YearMarkdownBox = styled.div`
  background: linear-gradient(90deg, #f4f7fa 0%, #e9eef6 100%);
  border-radius: 18px;
  box-shadow: 0 2px 12px rgba(52,81,130,0.08);
  padding: 2rem 1.5rem;
  margin-bottom: 2rem;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  overflow: hidden;
  font-size: 1.15rem;
  color: #2d3a4a;
  line-height: 1.7;
  font-weight: 400;
  h1, h2, h3, h4, h5, h6 {
    color: #345182;
    font-weight: 700;
    margin-top: 1.2em;
    margin-bottom: 0.7em;
  }
  ul, ol {
    margin-left: 1.5em;
    margin-bottom: 1em;
  }
  p {
    margin-bottom: 1em;
  }
  a {
    color: #345182;
    text-decoration: underline;
    &:hover {
      color: #2a8c42;
    }
  }
`;

interface DayLinkProps {
  hasIssues?: boolean;
}

const DayLink = styled(Link)<DayLinkProps>`
  color: #fff;
  display: inline-block;
  padding: 6px 12px;
  margin: 4px 3px;
  border-radius: 18px;
  background-color: ${props => props.hasIssues ? '#3a7346' : '#617284'};
  text-decoration: none;
  font-weight: 500;
  opacity: 0.9;
  vertical-align: middle;
  transition: all .2s ease-in-out; 
  box-shadow: ${props => props.hasIssues ? '0px 2px 6px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.2)'};
  letter-spacing: 0.3px;
  border: 1px solid ${props => props.hasIssues ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  
  ${props => props.hasIssues && `
    font-weight: 600;
    opacity: 1;
  `}
  
  :hover {
    text-decoration: none;
    background-color: ${props => props.hasIssues ? '#2a8c42' : '#345182'};
    color: #fff;
    opacity: 1;
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.25);
  }
  
  :active {
    transform: translateY(1px);
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

const PageTitle = styled.h2`
  margin: 0.25rem 0;
  font-size: 2rem;
  font-weight: 500;
`;

const BreadCrumbNav = styled.nav`
  background-color: #e9ecef;
`;

// Define interfaces for our data structures
interface DayInfo {
  day: number;
  date: string;
  displayDate: string;
}

interface MonthInfo {
  month: number;
  name: string;
  days: DayInfo[];
}

// GraphQL query - Note: this may need to be updated when backend is ready
const YEAR_QUERY = gql`
  query YearPage($year: Int!) {
    yil(year: $year) {
      content,
      daysWithIssues,
      commonTags(limit: 100) {
        tag
        count
      }
    }
  }
`;

type YearPageProps = RouteComponentProps<{ yil: string }>;

const YearPageComponent: React.FC<YearPageProps> = (props) => {
  const [showFullMarkdown, setShowFullMarkdown] = useState(false);
  const { yil } = props.match.params;
  const year = parseInt(yil, 10);
  const intl = useIntl();
  const fireCsEvent = useCsEvent();

  const { loading, data } = useQuery(YEAR_QUERY, {
    variables: { year },
    onError: () => {
      console.log("GraphQL query for year data failed - displaying calendar anyway");
    }
  });
  
  // Extract days with newspaper issues if available from backend
  const daysWithIssues = useMemo(() => {
    const days = new Set<string>();
    
    if (data?.yil?.daysWithIssues) {
      data?.yil?.daysWithIssues.forEach((day: string) => {
        days.add(day);
      });
    }
    
    return days;
  }, [data]);

  // Generate all days in the year
  const monthsWithDays = useMemo(() => {
    const months: MonthInfo[] = [];

    // Check if year is valid
    if (isNaN(year) || year < 1500 || year > 2100) {
      return months;
    }

    for (let month = 0; month < 12; month++) {
      const daysInMonth: DayInfo[] = [];
      const daysCount = new Date(year, month + 1, 0).getDate();
      
      for (let day = 1; day <= daysCount; day++) {
        const date = moment(new Date(year, month, day));
        const formattedDate = date.format('YYYY-MM-DD');
        daysInMonth.push({
          day,
          date: formattedDate,
          displayDate: date.format('D MMMM YYYY')
        });
      }
      
      months.push({
        month: month + 1,
        name: moment().month(month).format('MMMM'),
        days: daysInMonth
      });
    }
    
    return months;
  }, [year]);

  const handleDayClick = () => {
    fireCsEvent({
      category: "YearPage",
      action: "Navigation",
      label: "Day Clicked"
    });
  };
  
  const navigateToYear = (targetYear: number) => {
    props.history.push(`/yil/${targetYear}`);
  };

  const handleShowMore = () => {
    fireCsEvent({
      category: "YearPage",
      action: "Engagement",
      label: "Show More Pressed"
    });
    setShowFullMarkdown(true);
  };

  const handleShowLess = () => {
    fireCsEvent({
      category: "YearPage",
      action: "Engagement",
      label: "Show Less Pressed"
    });
    setShowFullMarkdown(false);
  };

  return (
    <Container>
      <Helmet>
        <title>{intl.formatMessage({id: "year.title"}, {year})}</title>
        <meta 
          name="description" 
          content={intl.formatMessage({id: "year.description"}, {year})} 
        />
      </Helmet>

      <BreadCrumbNav aria-label="breadcrumb">
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to="/">
              <FormattedMessage id="name" />
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem active>
            <FormattedMessage id="year.breadcrumb" defaultMessage="{year} Yılı" values={{year}} />
          </BreadcrumbItem>
        </Breadcrumb>
      </BreadCrumbNav>

      {loading && (
        <Row>
          <Col>
            <p><FormattedMessage id="loading" defaultMessage="Yükleniyor..." /></p>
          </Col>
        </Row>
      )}

      {/* Show year content in markdown if available */}
      {!loading && data?.yil?.content && (
        <YearMarkdownBox style={{maxHeight: showFullMarkdown ? 'none' : '360px', overflow: showFullMarkdown ? 'visible' : 'hidden', position: 'relative'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem'}}>
            <svg width="32" height="32" fill="none" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="#345182"/><path d="M16 8v8l6 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span style={{margin: 0, color: '#345182', fontWeight: 700, fontSize: '1.5rem'}}>
              <FormattedMessage id="year.importantEvents" defaultMessage="Önemli Olaylar" />
            </span>
          </div>
          <ReactMarkdown
            components={{
              a: ({node, ...props}) => (
                <a
                  {...props}
                  onClick={e => {
                    fireCsEvent({
                      category: "YearPage",
                      action: "Engagement",
                      label: "Markdown Link Click"
                    });
                    if (props.onClick) props.onClick(e);
                  }}
                >
                  {props.children}
                </a>
              )
            }}          
          >
            {data.yil.content}
          </ReactMarkdown>
          {!showFullMarkdown && (
            <div style={{position: 'absolute', left: 0, right: 0, bottom: 0, height: '80px', background: 'linear-gradient(0deg, #f4f7fa 80%, rgba(244,247,250,0.0) 100%)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center'}}>
              <button
                style={{
                  background: '#345182',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '18px',
                  padding: '8px 24px',
                  fontWeight: 500,
                  fontSize: '1rem',
                  boxShadow: '0 2px 8px rgba(52,81,130,0.08)',
                  cursor: 'pointer',
                  marginBottom: '12px',
                  zIndex: 2
                }}
                onClick={handleShowMore}
              >
                <FormattedMessage id="year.showMore" defaultMessage="Daha fazla göster" />
              </button>
            </div>
          )}
          {showFullMarkdown && (
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '1rem'}}>
              <button
                style={{
                  background: '#e9ecef',
                  color: '#345182',
                  border: 'none',
                  borderRadius: '18px',
                  padding: '6px 18px',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  boxShadow: '0 2px 8px rgba(52,81,130,0.04)',
                  cursor: 'pointer',
                  zIndex: 2
                }}
                onClick={handleShowLess}
              >
                <FormattedMessage id="year.showLess" defaultMessage="Daha az göster" />
              </button>
            </div>
          )}
        </YearMarkdownBox>
      )}

      <Row className="align-items-center mb-3">
        <Col>
          <PageTitle className="text-center mb-0">
            <FormattedMessage id="year.heading" defaultMessage="Arşivde {year} yılı" values={{year}} />
          </PageTitle>
        </Col>
      </Row>
      <Row>
        <UstBannerAd />
      </Row>

      {!loading && monthsWithDays.map((month) => (
        <MonthContainer key={`month-${month.month}`}>
          <MonthTitle>{month.name}</MonthTitle>
          <Row className="justify-content-center">
            {month.days.map((dayInfo) => {
              const hasIssues = daysWithIssues.has(dayInfo.date);
              return (
                <DayLink 
                  key={`day-${dayInfo.date}`}
                  to={`/tarih/${dayInfo.date}`}
                  onClick={handleDayClick}
                  title={hasIssues 
                    ? intl.formatMessage(
                        {id: "year.dayWithIssues"}, 
                        {date: dayInfo.displayDate}
                      ) 
                    : dayInfo.displayDate
                  }
                  hasIssues={hasIssues}
                  aria-label={hasIssues 
                    ? intl.formatMessage(
                        {id: "year.dayWithIssuesAriaLabel"}, 
                        {day: dayInfo.day, month: month.name, year}
                      )
                    : dayInfo.displayDate
                  }
                >
                  {dayInfo.day}
                </DayLink>
              );
            })}
          </Row>
        </MonthContainer>
      ))}

      {!loading && monthsWithDays.length === 0 && (
        <Row>
          <Col>
            <div className="alert alert-warning text-center" role="alert">
              <FormattedMessage id="year.invalidYear" />
            </div>
          </Col>
        </Row>
      )}

      {/* Legend */}
      <Row className="justify-content-center">
        <Col className="d-flex flex-wrap justify-content-center align-items-center">
          <div className="d-flex align-items-center mr-1 mb-2">
            <div style={{ 
              width: "20px", 
              height: "20px", 
              backgroundColor: "#617284", 
              borderRadius: "4px",
              marginRight: "8px"
            }}></div>
            <span><FormattedMessage id="year.legend.noissues"/></span>
          </div>
          <div className="d-flex align-items-center mb-2">
            <div style={{ 
              width: "20px", 
              height: "20px", 
              backgroundColor: "#3a7346", 
              borderRadius: "4px",
              marginRight: "8px"
            }}></div>
            <span><FormattedMessage id="year.legend.withIssues" /></span>
          </div>
        </Col>
      </Row>

      {/* Year navigation at bottom */}
      <Row className="my-4 justify-content-center">
        <Button 
          variant="outline-secondary"
          className="mr-2"
          onClick={() => {
            fireCsEvent({
              category: "YearPage",
              action: "Navigation",
              label: `Prev year clicked`
            });
            navigateToYear(year - 1)
          }}
          aria-label={`Go to year ${year - 1}`}
        >
          <BsChevronLeft /> {year - 1}
        </Button>
        <Button 
          variant="outline-secondary"
          className="ml-2"
          onClick={() => {
            fireCsEvent({
              category: "YearPage",
              action: "Navigation",
              label: `Next year clicked`
            });
            navigateToYear(year + 1)
          }}
          aria-label={`Go to year ${year + 1}`}
        >
          {year + 1} <BsChevronRight />
        </Button>
      </Row>
      {/* Common tags for the year */}
      {!loading && data?.yil?.commonTags && data.yil.commonTags.length > 0 && (
        <Row className="justify-content-center my-4">
          <Col md={10} lg={8}>
            <div style={{
              background: 'linear-gradient(90deg, #f4f7fa 0%, #e9eef6 100%)',
              borderRadius: '18px',
              boxShadow: '0 2px 12px rgba(52,81,130,0.08)',
              padding: '2rem 1.5rem',
              marginBottom: '2rem',
              textAlign: 'center',
            }}>
              <h3 style={{ color: '#345182', fontWeight: 700, marginBottom: '1.2rem' }}>
                <FormattedMessage id="year.commonTags" defaultMessage="Bu yılın ortak etiketleri" />
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                {data.yil.commonTags.map((tagObj: { tag: string, count: number }) => (
                  <Link
                    key={tagObj.tag}
                    to={`/etiket/${encodeURIComponent(tagObj.tag)}`}
                    style={{
                      background: '#345182',
                      color: '#fff',
                      borderRadius: '16px',
                      padding: '6px 14px',
                      margin: '2px',
                      fontWeight: 500,
                      fontSize: '1rem',
                      boxShadow: '0 2px 8px rgba(52,81,130,0.08)',
                      cursor: 'pointer',
                      letterSpacing: '0.2px',
                      opacity: 0.92,
                      transition: 'all .2s',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                    onClick={() => {
                      fireCsEvent({
                        category: "YearPage",
                        action: "Engagement",
                        label: "Etiket link click"
                      });
                    }}
                    title={`${tagObj.tag} (${tagObj.count})`}
                  >
                    {tagObj.tag} <span style={{fontSize: '0.85em', color: '#e9eef6', marginLeft: '4px'}}>({tagObj.count})</span>
                  </Link>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <MatchedContentAd slotId="8053917782" />
        </Col>
      </Row>
    </Container>
  );
};

export default withRouter(YearPageComponent);
